import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Upload, Check, Sparkles, ImagePlus, ShieldCheck, BadgeCheck, Clock, AlertTriangle, FileText, Users } from "lucide-react";
import api, { mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppNav from "@/components/AppNav";
import ImageCropDialog from "@/components/ImageCropDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const THEMES = ["#E91E8C", "#FF4FA3", "#F5C6D6", "#C2185B", "#2563EB", "#38BDF8", "#1E3A8A", "#FFFFFF"];

const COUNTRIES = ["Italia", "Francia", "Germania", "Spagna", "Paesi Bassi", "Belgio", "Austria", "Portogallo", "Irlanda", "Svizzera", "Regno Unito", "Stati Uniti", "Altro (UE)", "Altro (extra-UE)"];
const EU_COUNTRIES = ["Italia", "Francia", "Germania", "Spagna", "Paesi Bassi", "Belgio", "Austria", "Portogallo", "Irlanda", "Altro (UE)"];
const LUVME_COUNTRY = "Italia";
const DEFAULT_BILLING = {
  mode: "vat", country: "Italia", legal_name: "", vat_number: "", tax_code: "",
  address: "", city: "", zip: "", sdi_pec: "", vies_number: "", tax_regime: "",
  name: "", account_type: "iban", iban: "",
};

export default function Onboarding() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const avatarRef = useRef();
  const coverRef = useRef();
  const [cropState, setCropState] = useState(null);

  const pickImage = (file, field, aspect, round, title) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropState({ src: reader.result, field, aspect, round, title });
    reader.readAsDataURL(file);
  };

  const [form, setForm] = useState({
    display_name: "", bio: "", avatar_url: "", cover_url: "",
    social_links: { instagram: "", twitter: "" },
    theme_color: "#E91E8C", profile_type: "free", subscription_price: 9.99,
    categories: [],
    billing: { ...DEFAULT_BILLING },
  });
  const [allCategories, setAllCategories] = useState([]);
  const [verif, setVerif] = useState({ verification_status: "unverified", rejection_reason: null });
  const [vdocs, setVdocs] = useState({ id_front: "", id_back: "", selfie: "" });
  const [vUploading, setVUploading] = useState("");
  const [vSubmitting, setVSubmitting] = useState(false);
  const vRefs = { id_front: useRef(), id_back: useRef(), selfie: useRef() };

  const WIZARD_STEPS = [
    { n: 1, label: "Info base", tip: "I profili con una bio completa convertono meglio: racconta chi sei e cosa offri." },
    { n: 2, label: "Foto", tip: "Scegli una cover che rappresenti il tuo stile e una foto profilo nitida e riconoscibile." },
    { n: 3, label: "Categoria e tema", tip: "Le categorie giuste ti fanno scoprire dai fan interessati. Il tema colora il tuo profilo." },
    { n: 4, label: "Abbonamento", tip: "Un prezzo equo aumenta le conversioni. Puoi sempre modificarlo in seguito." },
    { n: 5, label: "Riepilogo", tip: "Controlla tutto, aggiungi i dati fiscali e la verifica identità, poi salva." },
  ];
  const [step, setStep] = useState(1);
  const totalSteps = WIZARD_STEPS.length;

  const autosave = async () => {
    try { await api.post("/creator/profile", form); } catch (e) {}
  };
  const goNext = async () => { await autosave(); setStep((s) => Math.min(totalSteps, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goBack = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const loadVerif = async () => {
    try {
      const { data } = await api.get("/verification/me");
      setVerif(data);
    } catch (e) {}
  };
  useEffect(() => { loadVerif(); }, []);

  const uploadVerifDoc = async (file, docType) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png", "webp", "pdf"].includes(ext)) {
      toast.error("Formato non supportato. Usa immagini (jpg, png, webp) o PDF.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) { toast.error("File troppo grande. Massimo 15MB."); return; }
    setVUploading(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const { data } = await api.post("/verification/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVdocs((d) => ({ ...d, [docType]: data.url }));
      toast.success("Documento caricato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload fallito");
    } finally {
      setVUploading("");
    }
  };

  const submitVerification = async () => {
    if (!vdocs.id_front || !vdocs.id_back || !vdocs.selfie) {
      toast.error("Carica documento fronte, retro e selfie prima di inviare.");
      return;
    }
    setVSubmitting(true);
    try {
      await api.post("/verification/submit", vdocs);
      toast.success("Documenti inviati", { description: "Il tuo profilo è ora in verifica." });
      setVdocs({ id_front: "", id_back: "", selfie: "" });
      await loadVerif();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nell'invio");
    } finally {
      setVSubmitting(false);
    }
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setAllCategories(data)).catch(() => {});
  }, []);

  const toggleCategory = (cat) => {
    setForm((f) => {
      const has = f.categories.includes(cat);
      if (has) return { ...f, categories: f.categories.filter((c) => c !== cat) };
      if (f.categories.length >= 2) {
        toast.error("Puoi selezionare massimo 2 categorie");
        return f;
      }
      return { ...f, categories: [...f.categories, cat] };
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/creator/me");
        if (data.profile) {
          setForm({
            display_name: data.profile.display_name || "",
            bio: data.profile.bio || "",
            avatar_url: data.profile.avatar_url || "",
            cover_url: data.profile.cover_url || "",
            social_links: data.profile.social_links || { instagram: "", twitter: "" },
            theme_color: data.profile.theme_color || "#E91E8C",
            profile_type: data.profile.profile_type || "free",
            subscription_price: data.profile.subscription_price || 9.99,
            categories: data.profile.categories || [],
            billing: { ...DEFAULT_BILLING, ...(data.profile.billing || {}) },
          });
        }
      } catch (e) {}
      setLoading(false);
    })();
  }, []);

  const upload = async (file, field) => {
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "image.jpg");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload fallito");
    } finally {
      setUploading("");
    }
  };

  const applyCrop = async (blob) => {
    const field = cropState.field;
    setCropState(null);
    await upload(blob, field);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/creator/profile", form);
      await refreshUser();
      toast.success("Profilo salvato!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]"><Loader2 className="w-8 h-8 text-[#E91E8C] animate-spin" /></div>;
  }

  return (
    <div className="flex bg-[#0D0D0D] min-h-screen">
      <AppNav />
      {cropState && (
        <ImageCropDialog
          open={!!cropState}
          src={cropState.src}
          aspect={cropState.aspect}
          round={cropState.round}
          title={cropState.title}
          onCancel={() => setCropState(null)}
          onCropped={applyCrop}
        />
      )}
      <main className="flex-1 p-5 sm:p-8 pb-24 md:pb-8 overflow-x-hidden">
        <div className="max-w-2xl">
          <h1 className="text-3xl font-black font-heading mb-1">Il tuo profilo creator</h1>
          <p className="text-white/50 mb-6 text-sm">Personalizza il tuo spazio e imposta la monetizzazione.</p>

          {/* Wizard progress */}
          <div className="mb-6" data-testid="wizard-progress">
            <div className="flex items-center justify-between mb-2">
              {WIZARD_STEPS.map((s) => (
                <button
                  key={s.n} type="button" onClick={() => setStep(s.n)} data-testid={`wizard-step-${s.n}`}
                  className={`flex flex-col items-center gap-1.5 flex-1 ${step >= s.n ? "text-[#E91E8C]" : "text-white/40"}`}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${step > s.n ? "bg-[#E91E8C] border-[#E91E8C] text-white" : step === s.n ? "border-[#E91E8C] text-[#E91E8C]" : "border-white/15"}`}>
                    {step > s.n ? <Check className="w-4 h-4" /> : s.n}
                  </span>
                  <span className="text-[10px] sm:text-xs font-medium text-center leading-tight">{s.label}</span>
                </button>
              ))}
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#E91E8C] transition-all duration-500" style={{ width: `${(step / totalSteps) * 100}%` }} />
            </div>
            <div className="mt-3 flex items-start gap-2 bg-[#E91E8C]/10 border border-[#E91E8C]/25 rounded-xl p-3" data-testid="wizard-tip">
              <Sparkles className="w-4 h-4 text-[#E91E8C] mt-0.5 shrink-0" />
              <p className="text-xs text-white/75">{WIZARD_STEPS[step - 1].tip}</p>
            </div>
          </div>

          {/* Cover + avatar preview */}
          {step === 2 && (
          <div className="rounded-3xl overflow-hidden border border-white/10 mb-6">
            <div className="relative h-40 bg-[#1A1A1A]">
              {form.cover_url ? (
                <img src={mediaUrl(form.cover_url)} alt="cover" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30">Nessuna copertina</div>
              )}
              <button
                onClick={() => coverRef.current.click()} data-testid="upload-cover"
                className="absolute top-3 right-3 glass px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 border border-white/10"
              >
                {uploading === "cover_url" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                Copertina
              </button>
              <input ref={coverRef} type="file" accept="image/*" hidden onChange={(e) => { pickImage(e.target.files[0], "cover_url", 16 / 9, false, "Adatta copertina"); e.target.value = ""; }} />
              <div className="absolute -bottom-8 left-5">
                <div className="relative">
                  <img
                    src={form.avatar_url ? mediaUrl(form.avatar_url) : "https://static.prod-images.emergentagent.com/jobs/e1d27419-dcf8-460a-a69e-abac75d96492/images/a0169b481d53693ae7687b06d5862caa82f8c398910db092f4d72fc45f0ffe1e.jpeg"}
                    alt="avatar" className="w-20 h-20 rounded-full object-cover border-4 border-[#0D0D0D] bg-[#1A1A1A]"
                  />
                  <button
                    onClick={() => avatarRef.current.click()} data-testid="upload-avatar"
                    className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#E91E8C] flex items-center justify-center border-2 border-[#0D0D0D]"
                  >
                    {uploading === "avatar_url" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  </button>
                  <input ref={avatarRef} type="file" accept="image/*" hidden onChange={(e) => { pickImage(e.target.files[0], "avatar_url", 1, true, "Adatta foto profilo"); e.target.value = ""; }} />
                </div>
              </div>
            </div>
            <div className="h-10" />
          </div>
          )}

          <div className="space-y-5">
            {step === 1 && (<>
            <div>
              <Label className="text-white/80">Nickname (pseudonimo)</Label>
              <Input
                value={form.display_name} data-testid="ob-name" maxLength={20}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
              />
              <p className="text-xs text-white/40 mt-1.5">Usa uno pseudonimo pubblico, non il tuo nome reale (3-20 caratteri, niente spazi).</p>
            </div>
            <div>
              <Label className="text-white/80">Bio</Label>
              <Textarea
                value={form.bio} data-testid="ob-bio" rows={3}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="mt-2 bg-[#1A1A1A] border-white/10 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                placeholder="Racconta chi sei..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Instagram</Label>
                <Input
                  value={form.social_links.instagram} data-testid="ob-instagram"
                  onChange={(e) => setForm({ ...form, social_links: { ...form.social_links, instagram: e.target.value } })}
                  className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                  placeholder="@username"
                />
              </div>
              <div>
                <Label className="text-white/80">Twitter / X</Label>
                <Input
                  value={form.social_links.twitter} data-testid="ob-twitter"
                  onChange={(e) => setForm({ ...form, social_links: { ...form.social_links, twitter: e.target.value } })}
                  className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                  placeholder="@username"
                />
              </div>
            </div>
            </>)}

            {step === 3 && (<>
            <div>
              <Label className="text-white/80 mb-2 block">Categorie <span className="text-white/40 font-normal">(scegline 1-2)</span></Label>
              <div className="flex flex-wrap gap-2" data-testid="ob-categories">
                {allCategories.map((cat) => {
                  const active = form.categories.includes(cat);
                  return (
                    <button
                      key={cat} type="button" onClick={() => toggleCategory(cat)}
                      data-testid={`ob-category-${cat}`}
                      className={`px-3.5 py-2 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "bg-[#E91E8C] border-[#E91E8C] text-white"
                          : "bg-transparent border-white/15 text-white/70 hover:border-[#E91E8C]/50 hover:text-white"
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-white/80 mb-2 block">Tema colore</Label>
              <p className="text-xs text-white/40 mb-3">Personalizza gli accenti del tuo profilo pubblico. Ora anche in tonalità blu.</p>
              <div className="flex flex-wrap gap-3">
                {THEMES.map((c) => (
                  <button
                    key={c} onClick={() => setForm({ ...form, theme_color: c })} data-testid={`theme-${c}`}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${form.theme_color === c ? "ring-2 ring-offset-2 ring-offset-[#0D0D0D] ring-white" : ""}`}
                    style={{ backgroundColor: c }}
                  >
                    {form.theme_color === c && <Check className={`w-4 h-4 ${c === "#FFFFFF" || c === "#F5C6D6" ? "text-black" : "text-white"}`} />}
                  </button>
                ))}
              </div>

              {/* Live preview */}
              <div className="mt-4 rounded-2xl border border-white/10 overflow-hidden" data-testid="theme-preview">
                <div className="h-10" style={{ background: `linear-gradient(90deg, ${form.theme_color}, transparent)` }} />
                <div className="p-4 bg-[#111] flex items-center gap-3 -mt-6">
                  <div className="w-12 h-12 rounded-full border-4 border-[#111] shrink-0" style={{ backgroundColor: form.theme_color, boxShadow: `0 0 18px ${form.theme_color}88` }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold font-heading truncate">{form.display_name || "Il tuo nome"}</p>
                    <div className="flex items-center gap-3 text-xs mt-0.5">
                      <span className="flex items-center gap-1" style={{ color: form.theme_color }}><Users className="w-3.5 h-3.5" /> 128</span>
                      <span className="flex items-center gap-1" style={{ color: form.theme_color }}><Sparkles className="w-3.5 h-3.5" /> 24 post</span>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-white px-3 py-1.5 rounded-full shrink-0" style={{ backgroundColor: form.theme_color }}>Abbonati</span>
                </div>
                <p className="text-[11px] text-white/40 px-4 pb-3">Anteprima: così appariranno gli accenti sul tuo profilo pubblico.</p>
              </div>
            </div>
            </>)}

            {step === 4 && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#E91E8C]" /> Abbonamento mensile</p>
                  <p className="text-xs text-white/50 mt-1">{form.profile_type === "subscription" ? "I fan pagano per accedere al tuo profilo" : "Profilo pubblico gratuito"}</p>
                </div>
                <Switch
                  checked={form.profile_type === "subscription"} data-testid="ob-subscription-toggle"
                  onCheckedChange={(v) => setForm({ ...form, profile_type: v ? "subscription" : "free" })}
                  className="data-[state=checked]:bg-[#E91E8C]"
                />
              </div>
              {form.profile_type === "subscription" && (
                <div className="mt-4">
                  <Label className="text-white/80">Prezzo mensile (€)</Label>
                  <Input
                    type="number" step="0.01" min="0" value={form.subscription_price} data-testid="ob-price"
                    onChange={(e) => setForm({ ...form, subscription_price: parseFloat(e.target.value) || 0 })}
                    className="mt-2 bg-[#0D0D0D] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C] max-w-[160px]"
                  />
                </div>
              )}
            </div>
            )}

            {step === 5 && (<>
            {/* Fiscal data (private) — modes A (P.IVA) / B (occasionale) */}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-5" data-testid="billing-section">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-[#E91E8C]" />
                <h2 className="font-semibold">Dati fiscali</h2>
              </div>
              <p className="text-xs text-white/40 mb-4">Necessari per ricevere i pagamenti. Dati <span className="text-white/70 font-semibold">privati</span>: visibili solo a te e all'amministrazione, mai sul profilo pubblico. Sezione separata dalla Verifica identità.</p>

              <div className="flex gap-2 mb-5">
                {[{ id: "vat", label: "Ho Partita IVA o società" }, { id: "occasional", label: "Prestazione occasionale" }].map((opt) => (
                  <button
                    key={opt.id} type="button" data-testid={`billing-mode-${opt.id}`}
                    onClick={() => setForm({ ...form, billing: { ...form.billing, mode: opt.id } })}
                    className={`flex-1 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold border transition-colors ${form.billing.mode === opt.id ? "bg-[#E91E8C] border-[#E91E8C] text-white" : "border-white/15 text-white/70 hover:border-[#E91E8C]/50"}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {(() => {
                const b = form.billing;
                const setB = (patch) => setForm({ ...form, billing: { ...b, ...patch } });
                const isIT = b.country === "Italia";
                const isEUnonIT = EU_COUNTRIES.includes(b.country) && b.country !== LUVME_COUNTRY;
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white/70 text-xs mb-1 block">Paese di residenza fiscale</Label>
                      <select
                        data-testid="billing-country" value={b.country}
                        onChange={(e) => setB({ country: e.target.value })}
                        className="w-full h-11 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 text-sm focus:border-[#E91E8C] outline-none"
                      >
                        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>

                    {b.mode === "vat" ? (
                      <>
                        <div>
                          <Label className="text-white/70 text-xs mb-1 block">Ragione sociale / Titolare</Label>
                          <Input data-testid="billing-legal-name" value={b.legal_name} onChange={(e) => setB({ legal_name: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="Es. Luvme S.r.l. / Mario Rossi" />
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-1 block">Partita IVA (o equivalente)</Label>
                          <Input data-testid="billing-vat" value={b.vat_number} onChange={(e) => setB({ vat_number: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="IT01234567890" />
                        </div>
                        {isIT && (
                          <div>
                            <Label className="text-white/70 text-xs mb-1 block">Codice fiscale</Label>
                            <Input data-testid="billing-tax-code" value={b.tax_code} onChange={(e) => setB({ tax_code: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="RSSMRA80A01H501U" />
                          </div>
                        )}
                        <div className="sm:col-span-2">
                          <Label className="text-white/70 text-xs mb-1 block">Indirizzo di fatturazione</Label>
                          <Input data-testid="billing-address" value={b.address} onChange={(e) => setB({ address: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="Via e numero civico" />
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-1 block">Città</Label>
                          <Input data-testid="billing-city" value={b.city} onChange={(e) => setB({ city: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="Città" />
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-1 block">CAP</Label>
                          <Input data-testid="billing-zip" value={b.zip} onChange={(e) => setB({ zip: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="00100" />
                        </div>
                        {isIT && (
                          <div className="sm:col-span-2">
                            <Label className="text-white/70 text-xs mb-1 block">Codice Destinatario SDI o PEC</Label>
                            <Input data-testid="billing-sdi" value={b.sdi_pec} onChange={(e) => setB({ sdi_pec: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="SDI (7 caratteri) o indirizzo PEC" />
                          </div>
                        )}
                        {isEUnonIT && (
                          <div className="sm:col-span-2">
                            <Label className="text-white/70 text-xs mb-1 block">Numero VIES</Label>
                            <Input data-testid="billing-vies" value={b.vies_number} onChange={(e) => setB({ vies_number: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="Numero iscrizione VIES" />
                            <p className="text-[11px] text-white/40 mt-1">Richiesto per operazioni intra-UE B2B.</p>
                          </div>
                        )}
                        <div className="sm:col-span-2">
                          <Label className="text-white/70 text-xs mb-1 block">Regime fiscale <span className="text-white/30">(opzionale)</span></Label>
                          <Input data-testid="billing-regime" value={b.tax_regime} onChange={(e) => setB({ tax_regime: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="Es. Regime forfettario" />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label className="text-white/70 text-xs mb-1 block">Nome e cognome</Label>
                          <Input data-testid="billing-name" value={b.name} onChange={(e) => setB({ name: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="Mario Rossi" />
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-1 block">Codice fiscale (o equivalente)</Label>
                          <Input data-testid="billing-tax-code" value={b.tax_code} onChange={(e) => setB({ tax_code: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="RSSMRA80A01H501U" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-white/70 text-xs mb-1 block">Indirizzo completo</Label>
                          <Input data-testid="billing-address" value={b.address} onChange={(e) => setB({ address: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder="Via, città, CAP" />
                        </div>
                        <div className="sm:col-span-2 flex items-start gap-2.5 bg-amber-400/10 border border-amber-400/30 rounded-xl p-3" data-testid="occasional-warning">
                          <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                          <p className="text-xs text-white/70">Questa modalità è valida solo per compensi occasionali e non continuativi, entro i limiti previsti dalla legge del tuo paese di residenza. Se la tua attività su Luvme diventa regolare, ti consigliamo di passare alla modalità con Partita IVA.</p>
                        </div>
                      </>
                    )}

                    {/* Payout — both modes */}
                    <div>
                      <Label className="text-white/70 text-xs mb-1 block">Tipo di conto</Label>
                      <select
                        data-testid="billing-account-type" value={b.account_type}
                        onChange={(e) => setB({ account_type: e.target.value })}
                        className="w-full h-11 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 text-sm focus:border-[#E91E8C] outline-none"
                      >
                        <option value="iban">IBAN</option>
                        <option value="altro">Altro</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs mb-1 block">{b.account_type === "iban" ? "IBAN" : "Dati bancari"}</Label>
                      <Input data-testid="billing-iban" value={b.iban} onChange={(e) => setB({ iban: e.target.value })} className="bg-[#0D0D0D] border-white/10" placeholder={b.account_type === "iban" ? "IT60X0542811101000000123456" : "Dettagli conto"} />
                    </div>
                  </div>
                );
              })()}
            </div>


            {/* Identity verification */}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-5" data-testid="verification-section">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-5 h-5 text-[#E91E8C]" />
                <h2 className="font-semibold">Verifica identità</h2>
                {verif.verification_status === "verified" && (
                  <span data-testid="verif-badge-verified" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#F5C6D6] bg-[#F5C6D6]/10 border border-[#F5C6D6]/30 px-2.5 py-1 rounded-full">
                    <BadgeCheck className="w-3.5 h-3.5" /> Verificato
                  </span>
                )}
                {verif.verification_status === "pending" && (
                  <span data-testid="verif-badge-pending" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-300 bg-amber-400/10 border border-amber-400/30 px-2.5 py-1 rounded-full">
                    <Clock className="w-3.5 h-3.5" /> In verifica
                  </span>
                )}
                {(verif.verification_status === "unverified" || verif.verification_status === "rejected") && (
                  <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-white/50 bg-white/5 border border-white/15 px-2.5 py-1 rounded-full">
                    Non verificato
                  </span>
                )}
              </div>
              <p className="text-xs text-white/50 mb-4">Verifica la tua identità per ottenere il badge e sbloccare la pubblicazione di contenuti a pagamento. I documenti sono privati e visibili solo al team di revisione.</p>

              {verif.verification_status === "rejected" && verif.rejection_reason && (
                <div data-testid="verif-rejected-banner" className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-400">Verifica rifiutata</p>
                    <p className="text-xs text-white/60 mt-0.5">Motivo: {verif.rejection_reason}</p>
                    <p className="text-xs text-white/40 mt-1">Puoi ricaricare i documenti e inviare di nuovo.</p>
                  </div>
                </div>
              )}

              {verif.verification_status === "pending" ? (
                <div className="flex items-center gap-3 bg-amber-400/10 border border-amber-400/30 rounded-xl p-3" data-testid="verif-pending-note">
                  <Clock className="w-4 h-4 text-amber-300 shrink-0" />
                  <p className="text-sm text-white/70">Documenti inviati. Il team li sta esaminando, riceverai presto un esito.</p>
                </div>
              ) : verif.verification_status === "verified" ? (
                <div className="flex items-center gap-3 bg-[#F5C6D6]/10 border border-[#F5C6D6]/30 rounded-xl p-3">
                  <BadgeCheck className="w-4 h-4 text-[#F5C6D6] shrink-0" />
                  <p className="text-sm text-white/70">La tua identità è verificata. Hai accesso a tutte le funzionalità.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { key: "id_front", label: "Documento (fronte)" },
                      { key: "id_back", label: "Documento (retro)" },
                      { key: "selfie", label: "Selfie con documento" },
                    ].map((d) => (
                      <div key={d.key}>
                        <button
                          type="button" onClick={() => !vUploading && vRefs[d.key].current.click()}
                          data-testid={`verif-upload-${d.key}`}
                          className="w-full aspect-video rounded-xl border border-dashed border-white/20 bg-[#0D0D0D] flex flex-col items-center justify-center gap-1 text-white/50 hover:border-[#E91E8C]/50 transition-colors overflow-hidden"
                        >
                          {vUploading === d.key ? (
                            <Loader2 className="w-5 h-5 animate-spin text-[#E91E8C]" />
                          ) : vdocs[d.key] ? (
                            <span className="flex items-center gap-1.5 text-[#E91E8C] text-xs font-medium"><Check className="w-4 h-4" /> Caricato</span>
                          ) : (
                            <><FileText className="w-5 h-5" /><span className="text-[11px]">Carica</span></>
                          )}
                        </button>
                        <p className="text-[11px] text-white/50 mt-1.5 text-center">{d.label}</p>
                        <input ref={vRefs[d.key]} type="file" accept="image/*,application/pdf,.pdf" hidden onChange={(e) => e.target.files[0] && uploadVerifDoc(e.target.files[0], d.key)} />
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-white/30 mt-2">Immagini (jpg, png, webp) o PDF · max 15MB · mock KYC dimostrativo</p>
                  <Button
                    onClick={submitVerification}
                    disabled={vSubmitting || !vdocs.id_front || !vdocs.id_back || !vdocs.selfie}
                    data-testid="verif-submit"
                    className="w-full mt-4 h-11 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] font-semibold gap-2 disabled:opacity-50"
                  >
                    {vSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-4 h-4" /> Invia per la verifica</>}
                  </Button>
                </>
              )}
            </div>

            </>)}

            {/* Wizard navigation */}
            <div className="flex items-center gap-3 pt-2" data-testid="wizard-nav">
              {step > 1 && (
                <Button type="button" variant="outline" onClick={goBack} data-testid="wizard-back" className="h-12 rounded-xl bg-transparent border-white/15 hover:bg-white/5 px-6">Indietro</Button>
              )}
              {step < totalSteps ? (
                <Button type="button" onClick={goNext} data-testid="wizard-next" className="flex-1 h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] font-semibold">
                  Avanti
                </Button>
              ) : (
                <Button
                  onClick={save} disabled={saving} data-testid="ob-save"
                  className="flex-1 h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] text-white font-semibold hover:shadow-[0_0_18px_rgba(233,30,140,0.5)] transition-all"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salva profilo"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
